/** @jsxRuntime classic */
/** @jsx jsx */
import * as React from 'react';
import {forwardRef, useEffect, useImperativeHandle, useState} from 'react';
import {InfoBox} from '@flexe/ui-components';
import {css, jsx} from '@emotion/react';
import axios from 'axios';
import DocumentUploadModal from '../../shared/DocumentUploadModal';
import {
  ApiResponse,
  ApiResponseV2,
  CompanyType,
  Document,
  DocumentNotableType,
  DocumentType,
  Reservation
} from '../../shared/CommonInterfaces';
import {InboundDocumentsRow} from '../../dropoffs/shared/InboundDocumentsRow';
import DocumentsService from '../../shared/services/DocumentsService';
import {Inbound} from './InboundInterfaces';
import InboundErrors from './InboundErrors';

export interface InboundDocumentsRef {
  handleDeleteDocument: () => Promise<void>;
  handleCancelButtonClicked: () => void;
}

interface Props {
  inbound: Inbound;
  reservation: Reservation;
  handleIsBolUploaded: (isUploaded: boolean) => void;
  isContainerDeliveryArrived: boolean;
  isShipper: boolean;
  isEditMode: boolean;
  onDeleteDocsFromUi?: (documentId: number) => void;
  handleRefreshPage?: () => void;
  documentsService: DocumentsService;
  authenticationToken?: string;

  onDocumentSaved(savedDoc: Document);
}

const InboundDocuments = forwardRef<InboundDocumentsRef, Props>((props, ref) => {
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState<boolean>(false);
  const [documentLoadErrors, setDocumentLoadErrors] = React.useState<string[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const {inbound, reservation, isShipper, isEditMode, handleRefreshPage, documentsService, authenticationToken} = props;
  const [documentIdsToDelete, setDocumentIdsToDelete] = useState<number[]>([]);

  const DOCUMENTS_ROUTE = '/api/v2/documents';

  useEffect(() => {
    loadDocuments();
  }, []);

  const handleSavedDoc = (savedDoc: Document) => {
    if (isShipper) {
      savedDoc.uploader = CompanyType.shipper;
    } else {
      savedDoc.uploader = CompanyType.warehouse;
    }
    setDocuments(documents.concat([savedDoc]));
    handleRefreshPage();
  };

  const handleDeleteDocsFromUi = (documentId: number) => {
    // Stage the ID for deletion upon "Update"
    setDocumentIdsToDelete((prevIds) => [...prevIds, documentId]);
    // Immediately remove from the UI for a responsive feel
    setDocuments((currentDocs) => currentDocs.filter((doc) => doc.id !== documentId));
  };

  useImperativeHandle(ref, () => ({
    handleDeleteDocument: async () => {
      if (documentIdsToDelete.length === 0) return;

      const deletePromises = documentIdsToDelete.map((id) =>
        documentsService.deleteDocument(DOCUMENTS_ROUTE, id, authenticationToken)
      );

      try {
        await Promise.all(deletePromises);
        setDocumentIdsToDelete([]); // Clear the list on success
        await loadDocuments(); // Reload this component's data
      } catch (error) {
        setDocumentLoadErrors(['Failed to delete one or more documents. Please try again.']);
        setDocumentIdsToDelete([]); // Clear the list on failure
        await loadDocuments(); // Reload to restore the UI to a consistent state
      }
    },
    handleCancelButtonClicked: () => {
      // On cancel, clear the staged deletions and reload the original document list
      setDocumentIdsToDelete([]);
      loadDocuments();
    }
  }));

  const toggleUploadModal = () => {
    setShowUploadDocumentModal(!showUploadDocumentModal);
  };

  const checkUploadedBol = (): boolean => {
    let isBolUploaded = false;
    documents.forEach((doc) => {
      if (doc.documentType === DocumentType.billOfLading) {
        isBolUploaded = true;
      }
    });
    if (isBolUploaded) {
      props.handleIsBolUploaded(true);
    }
    return isBolUploaded;
  };

  const loadDocuments = async () => {
    setDocumentLoadErrors([]);
    try {
      const [shipperDocs, warehouseDocs] = await axios.all([
        documentsService.getDocuments(CompanyType.shipper, reservation.id, DocumentNotableType.container, inbound.id),
        documentsService.getDocuments(CompanyType.warehouse, reservation.id, DocumentNotableType.container, inbound.id)
      ]);

      const updatedDocuments = [...shipperDocs, ...warehouseDocs];
      setDocuments(updatedDocuments);
    } catch (error) {
      handleDocumentsLoadError(['Failed to load documents'], error.response?.data);
    }
  };

  const handleDocumentsLoadError = (err: string[], response: ApiResponse<any> | ApiResponseV2 | null) => {
    if (response?.errors?.length > 0) {
      err[0] += ':';
      response.errors.map((error: {detail: string}) => err.push(error.detail));
    }
    setDocumentLoadErrors(err);
  };

  return (
    <React.Fragment>
      <div id="documents" className={isShipper ? 'inbound-warehouse-info' : 'warehouse-document-section'}>
        <div className="container-fluid">
          <div className="documents-container">
            <header>
              <div className="title-aligned">
                <h2>Documents</h2>
              </div>
              {!isEditMode && (
                <div className="title-aligned right">
                  <a data-testid="document-upload-button" className="add-documents" onClick={toggleUploadModal}>
                    <i className="fa fa-plus"></i> Upload document
                  </a>
                  <div id="document-upload-modal">
                    <DocumentUploadModal
                      documents={documents}
                      documentsRoute="/api/v2/documents"
                      notableType={DocumentNotableType.container}
                      notableId={inbound.id}
                      showUploadModal={showUploadDocumentModal}
                      toggleUploadModal={toggleUploadModal}
                      onDocumentSaved={handleSavedDoc}
                    />
                  </div>
                </div>
              )}
            </header>
            <InboundErrors errors={documentLoadErrors} />
            <table className="table sticky">
              <thead>
                <tr>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody
                css={css`
                  display: block;
                  max-height: 300px;
                  overflow: auto;
                `}
                data-testid="inbound-documents-table-body"
              >
                {documents.map((doc, idx) => {
                  return (
                    <InboundDocumentsRow
                      key={idx}
                      document={doc}
                      onDeleteDocsFromUi={handleDeleteDocsFromUi}
                      isEditMode={isEditMode}
                    />
                  );
                })}
              </tbody>
            </table>
            {!isShipper && !checkUploadedBol() && props.isContainerDeliveryArrived && (
              <InfoBox info="Upload Signed Bill of Lading" infoType="warning"></InfoBox>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
});

export default InboundDocuments;
